import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  DeleteOutlined,
  EyeInvisibleTwoTone,
  EyeTwoTone,
} from '@ant-design/icons';
import { Button, Popconfirm, Space, type TableProps, Tag } from 'antd';
import { type JSX } from 'react';

import { dayjsFromTimestamp } from '@client/services/common.service';
import { getTranslatedMessage } from '@client/utils/i18n.util';
import { actionField } from '@client/utils/table/action.util';
import { columnsCache } from '@client/utils/table/column.util';
import {
  columnFilter,
  type TColumnFilter,
} from '@client/utils/table/filter.util';
import { columnSorter } from '@client/utils/table/sorter.util';
import { indexColumn } from '@client/utils/table/table.util';
import { DEFAULT_DATE_TIME_FORMAT, tsToLocaleDateTime } from '@common/utils';

import type { TFilters, TSorts } from '@client/hooks/useTable';
import type { IMetadata, ELocales, IApiKey, ITimestamp } from '@common/types';

type TArgs = {
  locale: ELocales;
  sortedInfo: TSorts;
  filteredInfo: TFilters;
  entities: IApiKey[];
  disabled: boolean;
  onActivateApiKey?: (entityId: IApiKey['id']) => void;
  onDeactivateApiKey?: (entityId: IApiKey['id']) => void;
  onDelete?: (entityId: IApiKey['id']) => void;
};

export const columnsMetadata = (
  props: TArgs,
  filterIn: string[] = [],
): TableProps<IApiKey>['columns'] => {
  const {
    sortedInfo,
    entities,
    filteredInfo,
    locale,
    disabled,
    onDelete,
    onActivateApiKey,
    onDeactivateApiKey,
  } = props;

  const { cached, updateCached } = columnsCache<IApiKey>({
    sortedInfo,
    filteredInfo,
    entities,
    locale,
    filterIn,
  });

  if (cached) {
    return cached;
  }

  const t = getTranslatedMessage;

  const columns = [
    indexColumn,
    {
      title: t('form.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      align: 'center',
      render: (isActive: boolean) =>
        isActive ? (
          <Tag
            style={{ width: 100, textAlign: 'center' }}
            icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
            color="success"
          >
            {t('tenant.api.active.on')}
          </Tag>
        ) : (
          <Tag
            style={{ width: 100, textAlign: 'center' }}
            icon={<CloseCircleTwoTone twoToneColor="#f5222d" />}
            color="error"
          >
            {t('tenant.api.active.off')}
          </Tag>
        ),
    },
    {
      title: t('tenant.api.keys.usageCount'),
      dataIndex: 'usageCount',
      key: 'usageCount',
      align: 'center',
      width: 150,
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'usageCount',
      ) as TColumnFilter<IApiKey>),
      ...columnSorter(sortedInfo, 'usageCount', 'usageCount', 2),
    },
    {
      title: t('form.lastUsed'),
      width: 200,
      dataIndex: 'metadata',
      align: 'center',
      key: 'metadata.lastUsed',
      hidden: true,
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'metadata.lastUsed',
        (lastUsed: ITimestamp['lastUsed']) =>
          tsToLocaleDateTime(
            dayjsFromTimestamp(lastUsed, '_').format(DEFAULT_DATE_TIME_FORMAT),
          ),
      ) as TColumnFilter<IApiKey>),
      ...columnSorter(sortedInfo, 'metadata', 'metadata.lastUsed', 2),
      render: ({ lastUsed }: ITimestamp): JSX.Element => (
        <div>
          {lastUsed
            ? tsToLocaleDateTime(
                dayjsFromTimestamp(lastUsed, '_').format(
                  DEFAULT_DATE_TIME_FORMAT,
                ),
              )
            : null}
        </div>
      ),
    },
    {
      title: t('form.createdAt'),
      width: 200,
      dataIndex: 'metadata',
      align: 'center',
      key: 'metadata.createdAt',
      hidden: true,
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'metadata.createdAt',
        (createdAt: IMetadata['createdAt']) =>
          tsToLocaleDateTime(
            dayjsFromTimestamp(createdAt, '_').format(DEFAULT_DATE_TIME_FORMAT),
          ),
      ) as TColumnFilter<IApiKey>),
      ...columnSorter(sortedInfo, 'metadata', 'metadata.createdAt', 2),
      render: ({ createdAt }: IMetadata): JSX.Element => (
        <div>
          {createdAt
            ? tsToLocaleDateTime(
                dayjsFromTimestamp(createdAt, '_').format(
                  DEFAULT_DATE_TIME_FORMAT,
                ),
              )
            : null}
        </div>
      ),
    },
    {
      title: t('form.updatedAt'),
      width: 200,
      dataIndex: 'metadata',
      align: 'center',
      key: 'metadata.updatedAt',
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'metadata.updatedAt',
        (updatedAt: IMetadata['updatedAt']) =>
          tsToLocaleDateTime(
            dayjsFromTimestamp(updatedAt, '_').format(DEFAULT_DATE_TIME_FORMAT),
          ),
      ) as TColumnFilter<IApiKey>),
      ...columnSorter(sortedInfo, 'metadata', 'metadata.updatedAt', 3),
      render: ({ updatedAt }: IMetadata): JSX.Element => (
        <div>
          {updatedAt
            ? tsToLocaleDateTime(
                dayjsFromTimestamp(updatedAt, '_').format(
                  DEFAULT_DATE_TIME_FORMAT,
                ),
              )
            : null}
        </div>
      ),
    },
    {
      ...actionField(),
      render: (_: any, record: IApiKey) => (
        <Space size="middle">
          <Popconfirm
            placement="topRight"
            title={
              record.isActive
                ? t('tenant.api.keys.deactivate.confirm')
                : t('tenant.api.keys.activate.confirm')
            }
            onConfirm={() =>
              record.isActive
                ? onDeactivateApiKey(record.id)
                : onActivateApiKey(record.id)
            }
            okText={t('actions.yes')}
            cancelText={t('actions.no')}
            disabled={disabled}
          >
            <Button
              type="text"
              danger={record.isActive}
              icon={
                record.isActive ? (
                  <EyeInvisibleTwoTone twoToneColor={'#f5222d'} />
                ) : (
                  <EyeTwoTone twoToneColor={'#52c41a'} />
                )
              }
              disabled={disabled}
            />
          </Popconfirm>
          <Popconfirm
            placement="topRight"
            title={t('tenant.api.keys.delete.confirm')}
            onConfirm={() => onDelete(record.id)}
            okText={t('actions.yes')}
            cancelText={t('actions.no')}
            disabled={disabled}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={disabled}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const result = filterIn.length
    ? columns.filter(
        (column) =>
          filterIn.includes(column?.dataIndex as string) ||
          column.dataIndex === 'idx',
      )
    : columns;

  return updateCached(result);
};
